import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ApolloFeeService {

    constructor(private apollo: Apollo) { }

    getFeeList(): Observable<any[]> {
        const GET_FEELIST = gql`
            query {
                fees {
                    allFees(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            amount
                            isMandatory
                            refFeesType {
                                id
                                name
                            }
                            discountType {
                                id
                                name
                                value
                                type
                            }
                            otCalculation {
                                id
                                name
                                type
                            }
                            gradeIdList
                            schoolYear {
                                id
                                year
                                month
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                            grade {
                                id
                                name
                                isStaffSpecificStudent
                                school {
                                    id
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_FEELIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const feeListData = result?.data?.fees?.allFees?.data || [];
                return feeListData;
            })
        );
    }
    

    getFeeTypeList(): Observable<any[]> {
        const GET_FEETYPELIST = gql`
            query {
                refFeesType {
                    allRefFeesTypes(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_FEETYPELIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const feeTypeListData = result?.data?.refFeesType?.allRefFeesTypes?.data  || [];
                return feeTypeListData;
            })
        );
    }
    

    getDiscountTypeList(): Observable<any[]> {
        const GET_DISCOUNTTYPELIST = gql`
            query {
                discountType {
                    allDiscountTypes(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            value
                            type
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            schoolYear {
                                id
                                year
                                month
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_DISCOUNTTYPELIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const discountTypeListData = result?.data?.discountType?.allDiscountTypes?.data || [];
                return discountTypeListData;
            })
        );
    }


    getOTCalculationList(): Observable<any[]> {
        const GET_OTCALCULATIONLIST = gql`
            query {
                otCalculation {
                    allOTCalculations(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            type
                            calculationType
                            isOverTime
                            schoolYear {
                                id
                                year
                                month
                                school {
                                    id
                                    name
                                    description
                                    address
                                    postalCode
                                    city
                                    state {
                                        id
                                        name
                                    }
                                    weekend {
                                        id
                                        type
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_OTCALCULATIONLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const otCalculationListData = result?.data?.otCalculation?.allOTCalculations?.data || [];
                return otCalculationListData;
            })
        );
    }


    getPaymentList(month: number, year: number, gradeId?: any, classId?: any): Observable<any[]> {
        const normalizeMonth = (val: any): number => {
            const parsed = Number(val);
            return Number.isFinite(parsed) && parsed >= 0 && parsed <= 12 ? parsed : (new Date().getMonth() + 1);
        };
        const normalizeYear = (val: any): number => {
            const parsed = Number(val);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : (new Date().getFullYear());
        };
        const normalizeId = (val: any): number | null => {
            const parsed = Number(val);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        };

        const normalizedMonth = normalizeMonth(month);
        const normalizedYear = normalizeYear(year);
        const normalizedGradeId = normalizeId(gradeId);
        const normalizedClassId = normalizeId(classId);
        const GET_OTCALCULATIONLIST = gql`
            query {
                payment {
                    allPayments(pageNo: 1, month: ${normalizedMonth}, year: ${normalizedYear}, gradeId: ${normalizedGradeId}, classId: ${normalizedClassId}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            totalAmount
                            month
                            year
                            invoiceNumber 
                            refStatus {
                                id
                                name
                            }
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                    name
                                    isStaffSpecificStudent
                                    }
                                }
                                user {
                                    phoneNumber
                                }
                            }
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            schoolYear {
                                id
                                year
                                month
                            }
                            paymentDetails {
                                id
                                amount
                                paymentDate
                                paymentMethod
                                billCode
                                isDiscount
                                remark
                                createdDate
                                fees {
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                                refStatus {
                                    id
                                    name
                                }
                            }
                        }
                        totalPayment
                        totalStudent
                        totalPaid
                        totalPending
                    }
                }
            }
        `;

        // Use query instead of watchQuery to prevent multiple emissions that clear data
        return this.apollo.query<any>({
            query: GET_OTCALCULATIONLIST,
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                // Return the same structure as getPaymentListOnce for consistency
                const otCalculationListData = result?.data?.payment?.allPayments || { data: [], totalStudent: 0, totalPayment: 0, totalPaid: 0, totalPending: 0 };
                return otCalculationListData;
            })
        );
    }

    // One-time query for refreshing payment list (used after mutations)
    getPaymentListOnce(month: number, year: number, gradeId?: any, classId?: any): Observable<any> {
        const normalizeMonth = (val: any): number => {
            const parsed = Number(val);
            return Number.isFinite(parsed) && parsed >= 0 && parsed <= 12 ? parsed : (new Date().getMonth() + 1);
        };
        const normalizeYear = (val: any): number => {
            const parsed = Number(val);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : (new Date().getFullYear());
        };
        const normalizeId = (val: any): number | null => {
            const parsed = Number(val);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        };

        const normalizedMonth = normalizeMonth(month);
        const normalizedYear = normalizeYear(year);
        const normalizedGradeId = normalizeId(gradeId);
        const normalizedClassId = normalizeId(classId);
        const GET_OTCALCULATIONLIST = gql`
            query {
                payment {
                    allPayments(pageNo: 1, month: ${normalizedMonth}, year: ${normalizedYear}, gradeId: ${normalizedGradeId}, classId: ${normalizedClassId}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            totalAmount
                            month
                            year
                            invoiceNumber 
                            refStatus {
                                id
                                name
                            }
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    name
                                    grade {
                                    name
                                    isStaffSpecificStudent
                                    }
                                }
                                user {
                                    phoneNumber
                                }
                            }
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            schoolYear {
                                id
                                year
                                month
                            }
                            paymentDetails {
                                id
                                amount
                                paymentDate
                                paymentMethod
                                billCode
                                isDiscount
                                remark
                                createdDate
                                fees {
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                                refStatus {
                                    id
                                    name
                                }
                            }
                        }
                        totalPayment
                        totalStudent
                        totalPaid
                        totalPending
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_OTCALCULATIONLIST,
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                // Return the same structure as watchQuery for consistency
                const otCalculationListData = result?.data?.payment?.allPayments || { data: [], totalStudent: 0, totalPayment: 0, totalPaid: 0, totalPending: 0 };
                return otCalculationListData;
            })
        );
    }


    getPaymentHistory(studentId: number, year: number): Observable<any[]> {
        const GET_OTCALCULATIONLIST = gql`
            query {
                payment {
                    paymentHistory(pageNo: 1, studentId: ${studentId}, year: ${year}) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            studentId
                            totalAmount
                            month
                            year
                            invoiceNumber 
                            refStatus {
                                id
                                name
                            }
                            student {
                                id
                                firstName
                                lastName
                                gender
                                birthCertificateNo
                                myKidNo
                                dateOfBirth
                                isOku
                                isActive
                                refOkuType {
                                    name
                                    description
                                }
                                class {
                                    id
                                    name
                                    grade {
                                        name
                                        isStaffSpecificStudent
                                    }
                                }
                            }
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            schoolYear {
                                id
                                year
                                month
                            }
                            paymentDetails {
                                id
                                amount
                                paymentDate
                                paymentMethod
                                billCode
                                isDiscount
                                remark
                                createdDate
                                fees {
                                    id
                                    name
                                    amount
                                    isMandatory
                                    discountType {
                                        name
                                        value
                                        type
                                    }
                                    otCalculation {
                                        name
                                        type
                                        calculationType
                                        isOverTime
                                    }
                                }
                                refStatus {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_OTCALCULATIONLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const otCalculationListData = result?.data?.payment?.paymentHistory?.data || [];
                return otCalculationListData;
            })
        );
    }


    getMonthList(): Observable<any[]> {
        const GET_OTCALCULATIONLIST = gql`
            query {
                payment {
                    paymentMonths
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_OTCALCULATIONLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const otCalculationListData = result?.data?.payment?.paymentMonths || [];
                return otCalculationListData;
            })
        );
    }

    createFee(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null) {
                return `${key}: null`;
            }
            if (value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: []`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_FEE = gql`
            mutation {
                fees {
                    addFees(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.fees?.addFees;
                return fee;
            })
        );
    }
    

    createOTCalculation(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_FEE = gql`
            mutation {
                otCalculation {
                    addOTCalculation(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.otCalculation?.addOTCalculation;
                return fee;
            })
        );
    }
    

    createDiscountType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_FEE = gql`
            mutation {
                discountType {
                    addDiscountType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.discountType?.addDiscountType;
                return fee;
            })
        );
    }

    generatePayment(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_FEE = gql`
            mutation {
                payment {
                    generatePayment(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.payment?.generatePayment;
                return fee;
            })
        );
    }

    generateSchoolPayment(): Observable<any> {
        const GET_OTCALCULATIONLIST = gql`
            query {
                schoolPayment {
                    generateSchoolPayment {
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_OTCALCULATIONLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const otCalculationListData = result?.data?.schoolPayment?.generateSchoolPayment || [];
                return otCalculationListData;
            })
        );
    }

    assignPayment(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATE_FEE = gql`
            mutation {
                payment {
                    assignPayment(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: CREATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.payment?.assignPayment;
                return fee;
            })
        );
    }

    blastPaymentWhatsapp(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const BLAST_WHATSAPP = gql`
            mutation {
                payment {
                    blastWhatsappPayment(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: BLAST_WHATSAPP
        }).pipe(
            map(result => {
                const response = result?.data?.payment?.blastWhatsappPayment;
                return response;
            })
        );
    }


    updatePayment(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_FEE = gql`
            mutation {
                payment {
                    updatePayment(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.payment?.updatePayment;
                return fee;
            })
        );
    }
    
    createSchoolBill(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_FEE = gql`
            mutation {
                schoolPayment {
                    createBill(input: { ${inputString} }){
                        id
                        collection_Id
                        paid
                        state
                        amount
                        paid_Amount
                        email
                        mobile
                        name
                        url
                        reference_1_label
                        reference_1
                        reference_2_label
                        reference_2
                        redirect_url
                        callback_url
                        description
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.schoolPayment?.createBill;
                return fee;
            })
        );
    }

    createFPXConfigBill(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_FEE = gql`
            mutation {
                schoolFPXConfiguration {
                    createBill(input: { ${inputString} }){
                        id
                        collection_Id
                        paid
                        state
                        amount
                        paid_Amount
                        email
                        mobile
                        name
                        url
                        reference_1_label
                        reference_1
                        reference_2_label
                        reference_2
                        redirect_url
                        callback_url
                        description
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.schoolFPXConfiguration?.createBill;
                return fee;
            })
        );
    }

    updateFee(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_FEE = gql`
            mutation {
                fees {
                    updateFees(input: { ${inputString} }) {
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.fees?.updateFees;
                return fee;
            })
        );
    }
    

    updateOTCalculation(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_FEE = gql`
            mutation {
                otCalculation {
                    updateOTCalculation(input: { ${inputString} }) {
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.otCalculation?.updateOTCalculation;
                return fee;
            })
        );
    }
    

    updateDiscountType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATE_FEE = gql`
            mutation {
                discountType {
                    updateDiscountType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATE_FEE
        }).pipe(
            map(result => {
                const fee = result?.data?.discountType?.updateDiscountType;
                return fee;
            })
        );
    }
    

    deleteFee(id: number): Observable<any> {
        const DELETE_FEE = gql`
            mutation {
                fees {
                    deleteFees(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_FEE,
        }).pipe(
            map(result => {
                const feeDetail = result?.data?.fees?.deleteFees;
                return feeDetail;
            })
        );
    }
    

    deleteOTCalculation(id: number): Observable<any> {
        const DELETE_FEE = gql`
            mutation {
                otCalculation {
                    deleteOTCalculation(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_FEE,
        }).pipe(
            map(result => {
                const feeDetail = result?.data?.otCalculation?.deleteOTCalculation;
                return feeDetail;
            })
        );
    }
    

    deleteDiscountType(id: number): Observable<any> {
        const DELETE_FEE = gql`
            mutation {
                discountType {
                    deleteDiscountType(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_FEE,
        }).pipe(
            map(result => {
                const feeDetail = result?.data?.discountType?.deleteDiscountType;
                return feeDetail;
            })
        );
    }
    

    deletePayment(id: number): Observable<any> {
        const DELETE_FEE = gql`
            mutation {
                payment {
                    deletePayment(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_FEE,
        }).pipe(
            map(result => {
                const feeDetail = result?.data?.payment?.deletePayment;
                return feeDetail;
            })
        );
    }
    

    getFeeById(id: number): Observable<any> {
        const GET_FEE = gql`
            query {
                fees {
                    feesById(id: ${id}) {
                        id
                        name
                        amount
                        isMandatory
                        discountType {
                            id
                            name
                            value
                            type
                        }
                        refFeesType {
                            id
                            name
                        }
                        otCalculation {
                            id
                            name
                            type
                        }
                        schoolYear {
                            id
                            year
                            month
                            school {
                                id
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                        }
                        grade {
                            id
                            name
                            isStaffSpecificStudent
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_FEE,
            variables: { id: id },
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const feeDetail = result?.data?.fees?.feesById[0];
                return feeDetail;
            })
        );
    }
    

    getPaymentById(id: number): Observable<any> {
        const GET_FEE = gql`
            query {
                payment {
                    paymentById(id: ${id}) {
                        id
                        totalAmount
                        month
                        year
                        invoiceNumber 
                        refStatus {
                            id
                            name
                        }
                        student {
                            firstName
                            lastName
                            gender
                            birthCertificateNo
                            myKidNo
                            dateOfBirth
                            isOku
                            isActive
                            refOkuType {
                                name
                                description
                            }
                            class {
                                name
                                grade {
                                    name
                                    isStaffSpecificStudent
                                }
                            }
                        }
                        school {
                            id
                            name
                            description
                            address
                            postalCode
                            city
                            state {
                                id
                                name
                            }
                            weekend {
                                id
                                type
                            }
                        }
                        schoolYear {
                            id
                            year
                            month
                        }
                            paymentDetails {
                                id
                                amount
                                paymentDate
                                paymentMethod
                                billCode
                                isDiscount
                                remark
                                createdDate
                                fees {
                                    id
                                    name
                                amount
                                isMandatory
                                discountType {
                                    name
                                    value
                                    type
                                }
                                otCalculation {
                                    name
                                    type
                                    calculationType
                                    isOverTime
                                }
                            }
                            refStatus {
                                id
                                name
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_FEE,
            variables: { id: id }
        }).pipe(
            map(result => {
                const feeDetail = result?.data?.payment?.paymentById[0];
                return feeDetail;
            })
        );
    }

    private removeTypename(data: any): any {
        if (Array.isArray(data)) {
            return data.map(item => this.removeTypename(item));
        } else if (data !== null && typeof data === 'object') {
            const newObj: any = {};
            Object.keys(data).forEach(key => {
                if (key !== '__typename') {
                    newObj[key] = this.removeTypename(data[key]);
                }
            });
            return newObj;
        }
        return data;
    }
}
